/**
 * Who is planned on what.
 *
 * Planned, not booked. The hours here are an intention; the hours on the Time
 * entries page are a record. The two are compared on the project page, and
 * keeping them in separate registers is what stops somebody reading a plan as
 * an actual.
 *
 * The capacity rule lives on the server: overlapping allocations cannot sum
 * past a full week, and the refusal names the total and how many allocations
 * make it up. There is no copy of that arithmetic here — a second
 * implementation of a rule is a rule with two answers.
 */

import { useMemo, useState } from 'react';
import { Building2, CalendarDays, FolderKanban, Plus, User } from 'lucide-react';
import {
  Alert,
  ApiError,
  Button,
  Can,
  PageHeader,
  ServerTable,
  api,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  formatDuration,
  isPastDate,
  toast,
  type ServerColumn,
} from '@connect/web-shared';

import { AllocationDialog } from './allocation-dialog';

export interface AllocationRow {
  public_id: string;
  project_public_id: string;
  project_name: string;
  client_name: string;
  employee_public_id: string;
  employee_name: string;
  starts_on: string;
  ends_on: string;
  minutes_per_week: number;
  days_per_week: string;
  notes: string | null;
}

const ENDPOINT = '/allocations';

export function AllocationsPage() {
  const [planning, setPlanning] = useState(false);
  const [editing, setEditing] = useState<AllocationRow | null>(null);
  const [recentKey, setRecentKey] = useState<string | null>(null);

  const fetchRows = useMemo(() => createServerFetcher<AllocationRow>(ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT), []);

  const columns = useMemo<ServerColumn<AllocationRow>[]>(
    () => [
      {
        field: 'employee_name',
        header: 'Who',
        icon: <User className="size-3" />,
        filter: 'select',
        minWidth: 180,
        render: (row) => <span className="truncate font-medium">{row.employee_name}</span>,
      },
      {
        field: 'project_name',
        header: 'Project',
        icon: <FolderKanban className="size-3" />,
        filter: 'select',
        minWidth: 200,
      },
      {
        field: 'client_name',
        header: 'Client',
        icon: <Building2 className="size-3" />,
        filter: 'select',
        minWidth: 170,
      },
      {
        field: 'starts_on',
        header: 'From',
        icon: <CalendarDays className="size-3" />,
        filter: 'date',
        width: 125,
        format: (value) => formatDate(value as string),
      },
      {
        field: 'ends_on',
        header: 'Until',
        filter: 'date',
        width: 125,
        render: (row) => {
          // A plan that has already run out is worth marking: it is still in
          // the list and is no longer telling anybody anything.
          const past = isPastDate(row.ends_on);
          return (
            <span className={past ? 'text-faint line-through' : undefined}>
              {formatDate(row.ends_on)}
            </span>
          );
        },
      },
      {
        field: 'minutes_per_week',
        header: 'Per week',
        filter: 'number',
        numeric: true,
        width: 130,
        render: (row) => (
          <span className="tabular">
            {formatDuration(row.minutes_per_week)}
            <span className="text-faint"> · {row.days_per_week}d</span>
          </span>
        ),
      },
      { field: 'notes', header: 'Notes', filter: 'text', minWidth: 200, hiddenByDefault: true },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Allocations"
        description="Planned time. What somebody is committed to, before any of it is booked."
        actions={
          <Can permission="projects.allocation.create">
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
                setPlanning(true);
              }}
            >
              <Plus className="size-3.5" />
              Plan someone in
            </Button>
          </Can>
        }
      />

      <Alert tone="info">
        Overlapping allocations cannot add up to more than a full week. If one is refused, the
        message names the others that are already using the time.
      </Alert>

      <ServerTable<AllocationRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={['allocations']}
        stateKey="projects.allocations"
        searchPlaceholder="Search by person, project or client"
        viewField="employee_name"
        onView={(row) => {
          setEditing(row);
          setPlanning(true);
        }}
        recentKey={recentKey}
        onDelete={async (row) => {
          try {
            await api.delete(`${ENDPOINT}/${row.public_id}`);
            toast.success('Allocation removed');
          } catch (error) {
            toast.error(
              'Could not remove it',
              error instanceof ApiError ? error.message : undefined,
            );
            throw error;
          }
        }}
        deleteLabel={(row) => `${row.employee_name} on ${row.project_name}`}
        rowDetailFields={(row) => [
          { label: 'Who', value: row.employee_name },
          { label: 'Project', value: row.project_name },
          { label: 'Client', value: row.client_name },
          { label: 'From', value: formatDate(row.starts_on) },
          { label: 'Until', value: formatDate(row.ends_on) },
          { label: 'Per week', value: `${formatDuration(row.minutes_per_week)} (${row.days_per_week} days)` },
          { label: 'Notes', value: row.notes },
        ]}
        emptyTitle="Nothing planned"
        emptyDescription="An allocation says somebody is committed to a project for part of their week, between two dates."
        emptyAction={
          <Can permission="projects.allocation.create">
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
                setPlanning(true);
              }}
            >
              <Plus className="size-3.5" />
              Plan someone in
            </Button>
          </Can>
        }
      />

      <AllocationDialog
        open={planning}
        allocation={editing}
        onClose={() => setPlanning(false)}
        onSaved={(publicId) => {
          setRecentKey(publicId);
          setPlanning(false);
        }}
      />
    </div>
  );
}
